import type { ReactNode } from "react";
import { getUnitCategoryName } from "verdi-common/i18n";
import { useSupportedLocale } from "@/hooks/use-locale";
import { ICON_KEYS, iconRegistry } from "@/icons/icon-registry";
import { cn } from "@/utils/class-name";

const DeviceIcon = iconRegistry[ICON_KEYS.DEVICE];

/**
 * The badge sets the card's heading, the legend sits under the plot — so the same leading-then-
 * qualifier ordering is drawn at two sizes.
 */
const VARIANTS = {
    badge: {
        // Each part wraps onto its own row when it doesn't fit beside the one before, then truncates.
        leading: "max-w-full truncate text-[13px] font-semibold text-foreground leading-[1.3]",
        trailing: "max-w-full truncate text-[12px] font-normal text-muted-foreground leading-[1.3]",
        glyph: "size-[12px]",
        // No dot: the parts wrap onto their own rows, and a separator would be left stranded at a row end.
        separator: false,
    },
    legend: {
        leading: "text-[11px] font-medium text-foreground leading-none",
        trailing: "text-[11px] font-normal text-muted-foreground leading-none",
        glyph: "size-[10px]",
        separator: true,
    },
} as const;

/**
 * Up to four names — metric, device, sensor, channel — with the first leading and the rest smaller
 * and dimmer. Shared so the metric badge and the legend read the same, since which of them lands
 * where depends on the panel (see planPanelChrome).
 */
export function SeriesLabel({
    unitCategory,
    showUnitCategory = true,
    deviceName,
    sensorClusterName = null,
    sensorElementName = null,
    variant = "legend",
}: {
    unitCategory: string | null;
    /** Off where the panel badge already names the metric. */
    showUnitCategory?: boolean;
    deviceName: string | null;
    sensorClusterName?: string | null;
    sensorElementName?: string | null;
    variant?: keyof typeof VARIANTS;
}) {
    const locale = useSupportedLocale();
    const type = VARIANTS[variant];

    const categoryName = unitCategory ? getUnitCategoryName(unitCategory, locale) : null;

    const hasOtherName = !!((showUnitCategory && categoryName) || deviceName || sensorClusterName);

    // Most channels are named after their metric, so showing both reads "Temperature · Temperature".
    // Kept where it is the row's only name, which would otherwise come out blank.
    const elementName = hasOtherName && sensorElementName === categoryName ? null : sensorElementName;

    // inline-flex, not flex: a block would take a line of its own instead of wrapping with the text
    // beside it.
    const device = deviceName ? (
        <span className={"inline-flex max-w-full items-center gap-1.5 align-center"}>
            <span className={"truncate"} title={deviceName}>
                {deviceName}
            </span>
            {/* Sized by class, not the size prop: Button's svg rule beats the attribute in the badge. */}
            <DeviceIcon className={cn("shrink-0", type.glyph)} />
        </span>
    ) : null;

    const parts: { key: string; node: ReactNode }[] = [
        { key: "category", node: showUnitCategory ? categoryName : null },
        { key: "device", node: device },
        { key: "sensorCluster", node: sensorClusterName },
        { key: "sensorElement", node: elementName },
    ].filter((part) => part.node !== null);

    // Whichever name we have leads. The rest only shrink because they are following it.
    const [leading, ...trailing] = parts;
    if (!leading) return null;

    return (
        <>
            <span className={type.leading}>{leading.node}</span>
            {trailing.map((part) => (
                <span key={part.key} className={type.trailing}>
                    {/* nbsp after the dot: keeps the separator with the name it introduces. */}
                    {type.separator ? " ·\u00a0" : null}
                    {part.node}
                </span>
            ))}
        </>
    );
}
